import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';
import { createMCPBridgeConnection } from './MCPBridgeConnection';
import type { MCPBridgeConnection } from './MCPBridgeConnection';
import { mcpBridgeStatusAtom, type MCPBridgeStatus } from './mcpBridgeStatusAtom';

type ViewAction = 'connect' | 'disconnect';

type ButtonView = {
  label: ParseKeys;
  disabled: boolean;
  action: ViewAction | null;
};

export function getView(status: MCPBridgeStatus): ButtonView {
  switch (status.kind) {
    case 'connecting':
      return { label: 'viewModel.mcpBridge.connecting', disabled: true, action: null };
    case 'connected':
      return { label: 'viewModel.mcpBridge.disconnect', disabled: false, action: 'disconnect' };
    case 'disconnected':
      return { label: 'viewModel.mcpBridge.connect', disabled: false, action: 'connect' };
    default: {
      // status が never でない場合（未処理の kind がある場合）にコンパイルエラーになる
      const _exhaustive: never = status;
      // 型アサーションや外部データで never 想定が破れた場合の実行時保険
      throw new Error(`Unhandled MCPBridgeStatus: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

export function MCPBridgeButton() {
  const { t } = useTranslation();
  const status = useAtomValue(mcpBridgeStatusAtom);
  const view = getView(status);
  const connectionRef = useRef<MCPBridgeConnection | null>(null);
  // NOTE: createMCPBridgeConnection は現状純粋なファクトリで副作用を持たないため、レンダー中の
  //   遅延初期化は安全。将来 constructor 内で接続開始やログ出力などの副作用を持たせる場合は、
  //   並行レンダリングで破棄される render のインスタンスがリークしうるため、onClick の connect
  //   アクション実行時に遅延生成する方式への切り替えを検討する。
  if (connectionRef.current === null) {
    connectionRef.current = createMCPBridgeConnection();
  }

  useEffect(() => {
    return () => {
      connectionRef.current?.disconnect();
      connectionRef.current = null;
    };
  }, []);

  return (
    <button
      className="flex w-full items-center justify-center gap-1 rounded border border-brand bg-white px-4 py-2 text-sm font-semibold text-brand hover:bg-brand-light disabled:cursor-not-allowed disabled:opacity-50"
      onClick={() => {
        if (view.action === null) return;
        switch (view.action) {
          case 'connect':
            connectionRef.current?.connect();
            break;
          case 'disconnect':
            connectionRef.current?.disconnect();
            break;
          default: {
            const _exhaustive: never = view.action;
            throw new Error(`Unhandled ViewAction: ${_exhaustive}`);
          }
        }
      }}
      disabled={view.disabled}
    >
      {t(view.label)}
    </button>
  );
}
